import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import NoResultsPlaceholder from 'components/NoResultsPlaceholder/NoResultsPlaceholder';
import Spinner from 'components/Spinner/Spinner';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { LoadingMembersStatusEnum } from 'modules/Workspaces/WorkspacesProvider/utils';
import { SetState } from 'utils/types';

import MemberListItem from './MemberListItem/MemberListItem';
import MembersListErrorBlock from './MembersListErrorBlock';
import { sortMembers } from './utils';
import useWorkspaceMembers from '../useWorkspaceMembers';

interface MembersListProps {
  searchValue: string;
  onMemberSelect: SetState<string | null>;
}

const MembersList = ({ searchValue, onMemberSelect }: MembersListProps) => {
  const { t } = useTranslation();
  const members = useWorkspaceMembers();
  const { loadingMembersStatus } = useWorkspacesContext();

  const filteredMembers = useMemo(() => {
    if (!searchValue) return sortMembers(members);

    const normalizedSearch = searchValue.toLowerCase();
    const foundMembers = members.filter(({ name, email, phone, id }) => {
      const searchFields = [name, email, phone, id];
      return searchFields.some((field) => field?.toLowerCase().includes(normalizedSearch));
    });

    return sortMembers(foundMembers);
  }, [members, searchValue]);

  if ([LoadingMembersStatusEnum.LOADING, LoadingMembersStatusEnum.READY].includes(loadingMembersStatus)) {
    return <Spinner variant="gray" className="mx-auto my-14" />;
  }

  if (loadingMembersStatus === LoadingMembersStatusEnum.ERROR) return <MembersListErrorBlock />;
  if (!filteredMembers.length) {
    return (
      <NoResultsPlaceholder className="bg-white" title={t('workspaces.no_results')}>
        {t('workspaces.no_members_found_desc')}
      </NoResultsPlaceholder>
    );
  }

  return filteredMembers.map((member) => <MemberListItem key={member.id} member={member} onClick={onMemberSelect} />);
};

export default MembersList;
